/* eslint-disable fp/no-mutation */
import React, { ReactElement, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material'
import {
  ContentType,
  contentTypes,
  FileData,
} from 'src/v3/utilities/filesystem'
import { CloseIcon, CloudUploadIcon, EditIcon } from 'src/v3/core/icons'

import { PinturaEditor } from '@pqina/react-pintura'
import { getEditorDefaults } from '@pqina/pintura'

import '@pqina/pintura/pintura.css'
import axios from 'axios'

let ImageUpload = ({
  type,
  image,
  setImage,
  existingImage,
  imageBasePath = 'images',
}: {
  type: 'banner' | 'thumbnail' | 'landscape' | 'default'
  image: File | undefined
  setImage: React.Dispatch<React.SetStateAction<File | undefined>>
  existingImage?: string
  imageBasePath?: 'images' | 'public'
}): ReactElement => {
  let [fileToUpload, setFileToUpload] = useState<FileData | undefined>()
  let [imgCropModal, setImgCropModal] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string | undefined>('')

  let handleCapture = (event: React.ChangeEvent) => {
    let file = (event.target as HTMLInputElement).files?.[0]
    let fileName = file?.name || ''
    let reader = new FileReader()

    reader.onloadend = () => {
      let fileData = reader.result
        ?.toString()
        ?.match(/data:[a-z]+\/[a-z]+/)?.[0]
      if (!fileData) {
        setErrorMessage('Image upload failed: read file failed')
        return
      }

      let fileType = fileData.split(':')[1]
      let fileExtension = fileType.split('/')[1]

      if (!contentTypes.includes(fileType as ContentType)) {
        setErrorMessage(`Invalid file type: ${fileType}`)
        return
      }

      let base64 = reader.result?.toString().split(',')[1] as string
      let binaryData = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
      let blobData = new Blob([binaryData], { type: fileExtension })

      setFileToUpload({
        name: fileName,
        type: fileType,
        extension: fileExtension,
        file: blobData,
      })
      setImage(undefined)
      setImgCropModal(true)
    }

    if (file) {
      reader.readAsDataURL(file)
    } else {
      setErrorMessage('No file selected')
    }
  }

  let dropHandler = (event: React.DragEvent) => {
    event.preventDefault()

    if (event.dataTransfer.items) {
      let items = [...event.dataTransfer.items]

      items.forEach((item) => {
        if (item.kind === 'file') {
          let file = item.getAsFile()
          if (file) {
            let fileData: FileData = {
              name: file.name,
              type: file.type,
              extension: file.type.split('/')[1],
              file,
            }

            if (['png', 'jpg', 'jpeg'].includes(fileData.extension)) {
              setFileToUpload(fileData)
              setImage(undefined)
              setImgCropModal(true)
            } else {
              setErrorMessage(`Invalid file type: ${fileData.extension}`)
            }
          }
        }
      })
    } else {
      setErrorMessage('File upload failed')
    }
  }

  let name = `event-${type}-image`
  let aspectRatio: Record<typeof type, number | undefined> = {
    thumbnail: 16 / 9,
    banner: 4 / 1,
    landscape: 11 / 8.5,
    default: undefined,
  }

  return (
    <div
      onDrop={dropHandler}
      onDragOver={(ev: React.DragEvent) => ev.preventDefault()}
    >
      <input
        type="file"
        accept=".png,.jpg,.jpeg"
        id={name}
        style={{ display: 'none' }}
        onChange={(e) => handleCapture(e)}
      />
      <Box
        sx={{
          borderWidth: 2,
          borderStyle: 'dashed',
          borderColor: 'background.paper',
          borderRadius: 2,
          bgcolor: '#0b0b0b',
          px: 2,
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <CloudUploadIcon sx={{ color: 'grey', height: 40, width: 60 }} />
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ mt: 1, mb: 0.5 }}
        >
          Drag and drop your image here, or
        </Typography>
        <label style={{ marginTop: 0 }} htmlFor={name}>
          <Button
            component="span"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            browse files
          </Button>
        </label>
        <Typography variant="subtitle2" sx={{ color: '#787878' }}>
          Maximum file size: 5MB
        </Typography>
      </Box>
      {(existingImage || image) && !imgCropModal && (
        <Card
          variant="outlined"
          sx={{
            mt: 1,
            p: 2,
            pr: 3,
            bgcolor: 'background.paperDark',
            display: 'grid',
            gridTemplateColumns: '1fr 40px',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 3,
            }}
          >
            <img
              style={{ height: 80, border: '1px solid #6d6d6d' }}
              src={
                image
                  ? URL.createObjectURL(image)
                  : imageBasePath === 'public'
                    ? `${import.meta.env.VITE_CLOUDFRONT_URL}/public/${existingImage}`
                    : `${import.meta.env.VITE_CLOUDFRONT_URL}/images/${existingImage?.replace('originals', 'resized')}`
              }
            />
            {fileToUpload && (
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  {fileToUpload?.name}
                </Typography>
                {image && (
                  <Typography variant="subtitle2" color="textSecondary">
                    {Math.round(image.size / 10000) / 100}MB
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <IconButton
            onClick={() => {
              if (!fileToUpload) {
                axios
                  .get(
                    imageBasePath === 'public'
                      ? `${import.meta.env.VITE_CLOUDFRONT_URL}/public/${existingImage}`
                      : `${import.meta.env.VITE_DIRECT_IMAGE_URL}/images/${existingImage?.replace('originals', 'resized')}`,
                    { responseType: 'blob' },
                  )
                  .then((response) =>
                    setFileToUpload({
                      name: `event-${type}.jpg`,
                      extension: 'jpg',
                      type: 'image/jpg',
                      file: response.data,
                    }),
                  )
                  .catch((err) =>
                    setErrorMessage(
                      err?.message ||
                        err.response?.data ||
                        `Error fetching ${type} image`,
                    ),
                  )
              }
              setImgCropModal(true)
            }}
          >
            <EditIcon />
          </IconButton>
        </Card>
      )}
      <Dialog
        open={imgCropModal && !!(fileToUpload || existingImage)}
        fullWidth
      >
        <Card variant="outlined" sx={{ px: 2, py: 3, overflow: 'scroll' }}>
          <IconButton
            sx={{ position: 'absolute', right: 16, top: 16 }}
            onClick={() => setImgCropModal(false)}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            variant="h5"
            textAlign="center"
            sx={{ mb: type === 'default' ? 1.5 : 0 }}
          >
            Crop Image
          </Typography>
          {type !== 'default' && (
            <Typography
              variant="subtitle2"
              color="textSecondary"
              textAlign="center"
              sx={{ mb: 0.5, mt: 1 }}
            >
              All {type} images must have a {type === 'banner' ? '4:1' : '16:9'}{' '}
              aspect ratio.
            </Typography>
          )}
          <div style={{ height: 500 }}>
            <PinturaEditor
              {...getEditorDefaults()}
              src={image || fileToUpload?.file || ''}
              onProcess={(res) => {
                setImage(res.dest)
                setImgCropModal(false)
              }}
              imageCropAspectRatio={aspectRatio[type]}
            />
          </div>
        </Card>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  )
}

export default ImageUpload
